import { Box, TextField } from "@mui/material";
import ChatCommands from "./ChatCommands";
import { useRef, useState } from "react";
import { useFloating, offset } from "@floating-ui/react";

interface ChatFieldProps {
  botAnswering: boolean;
  setPrompt: (prompt: string) => void;
  prompt: string;
  formRef: React.RefObject<HTMLFormElement>;
}

const getCaretCoordinates = (
  element: HTMLTextAreaElement,
  position: number | null
) => {
  // Create a dummy div to mirror the textarea content
  const div = document.createElement("div");
  document.body.appendChild(div);

  // Mirror the textarea style and content to the dummy div
  const style = getComputedStyle(element);
  const propsToMirror = [
    "width",
    "height",
    "font",
    "paddingLeft",
    "paddingTop",
    "paddingBottom",
    "paddingRight",
    "borderLeftWidth",
    "borderRightWidth",
    "borderTopWidth",
    "borderBottomWidth",
    "lineHeight",
    "textAlign",
    "letterSpacing",
    "wordSpacing",
  ];
  propsToMirror.forEach((prop: any) => (div.style[prop] = style[prop]));
  div.textContent = element.value.substring(0, position || 0);

  // Position the div exactly over the textarea and make it invisible
  div.style.position = "absolute";
  div.style.visibility = "hidden";
  div.style.whiteSpace = "pre-wrap";
  div.style.wordWrap = "break-word";
  div.style.overflow = "scroll";
  div.scrollTop = element.scrollTop; // Adjust for vertical scroll
  div.scrollLeft = element.scrollLeft; // Adjust for horizontal scroll

  // Create a span at the caret's position
  const span = document.createElement("span");
  span.textContent = element.value.substring(position || 0) || ".";
  div.appendChild(span);

  // Calculate offsets, accounting for element scroll positions
  const { offsetLeft, offsetTop } = span;
  const adjustedLeft = offsetLeft - element.scrollLeft;
  const adjustedTop = offsetTop - element.scrollTop;

  document.body.removeChild(div);

  // Adjust final coordinates to account for any fixed offsets you need (e.g., padding)
  const PADDING_OFFSET = { top: 20, left: 10 };
  return {
    top: adjustedTop + PADDING_OFFSET.top,
    left: adjustedLeft + PADDING_OFFSET.left,
  };
};

const ChatField: React.FC<ChatFieldProps> = ({
  botAnswering,
  setPrompt,
  prompt,
  formRef,
}) => {
  const [visible, setVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({
    mainAxis: 0,
    crossAxis: 0,
  });
  // const [command, setCommand] = useState<string>("");

  const commandListRef = useRef<HTMLLIElement>(null);
  const promptRef = useRef<HTMLTextAreaElement>(null);

  const { refs, floatingStyles } = useFloating({
    placement: "top-start",
    strategy: "absolute",
    // transform: false,
    open: visible,
    middleware: [offset(menuPosition)],
  });

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // console.log(e);
    const inputText = e.target.value;

    const shouldShowCommands = /(?:^|\s)(\/\S+)$|(?:^|\s)\/$/.test(inputText); // regexr.com/7up7h
    const inputCommand = /\/(\w+)(?=\s*$)/.exec(inputText)?.[1] || ""; // regexr.com/7up86
    console.log(inputCommand);

    if (shouldShowCommands && !visible) {
      const pos = getCaretCoordinates(e.target, e.target.selectionEnd);
      setMenuPosition({ crossAxis: pos.left, mainAxis: -pos.top });
    }

    // setCommand(inputCommand);

    setVisible(shouldShowCommands);

    setPrompt(inputText);
  };

  return (
    <>
      <TextField
        disabled={botAnswering}
        name="prompt"
        multiline
        fullWidth
        rows={4}
        placeholder="Ask Kiwi... You can type / to see available commands."
        value={prompt}
        onChange={handleChange}
        ref={refs.setReference}
        inputRef={promptRef}
        onKeyDown={(e) => {
          // console.log(e);
          // Check if Enter key is pressed, but not when Shift or other modifiers are used
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault(); // Prevent the default action to avoid a new line being added to the textarea
            formRef.current?.dispatchEvent(
              new Event("submit", {
                cancelable: true,
                bubbles: true,
              })
            );
          }
          if (visible && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
            e.preventDefault();
            if (commandListRef.current) commandListRef.current.focus();
          }
        }}
      />
      <Box hidden={!visible} ref={refs.setFloating} style={floatingStyles}>
        <ChatCommands
          prompt={prompt}
          setPrompt={setPrompt}
          //   command={command}
          //   setCommand={setCommand}
          visible={visible}
          setVisible={setVisible}
          promptRef={promptRef}
          listRef={commandListRef}
        />
      </Box>
    </>
  );
};

export default ChatField;
